import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { provideIonicAngular } from '@ionic/angular/standalone';
import {
  provideTranslateService,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';
import { APP_ROUTES } from '@app/app.routes';
import { LabelTranslationClientService } from '@services/label-translation-client.service';
import { DynamicRouteLoaderService } from '@services/dynamic-route-loader.service';
import { IonicStorageModule } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { provideAuth } from '@auth/providers/auth.providers';
import { provideHttpInterceptors } from '@interceptors/http-interceptors.providers';
import { authInterceptor } from '@auth/interceptors/auth.interceptor';
import {
  BASE_PATH,
  getRuntimeProinfraConfig,
  provideProinfraConfig,
} from '@profi/proinfra-shared-angular';

// Supported languages with fallback logic
const SUPPORTED_LANGUAGES = ['de', 'en'];

/**
 * App initializer factory to set up the translation service and dynamic routes.
 * Uses the browser's language preference or defaults to German ('de').
 * Logs errors during initialization without blocking app startup.
 */
export function initializeAppFactory(): () => Observable<any> {
  return () => {
    const translateService = inject(TranslateService);
    const routeClientService = inject(DynamicRouteLoaderService);

    const userLang: string = navigator.language || 'de';
    const languageCode = SUPPORTED_LANGUAGES.includes(userLang.split('-')[0])
      ? userLang.split('-')[0]
      : 'de'; // fallback language

    translateService.setDefaultLang('de');

    return forkJoin({
      translation: translateService.use(languageCode),
      routes: routeClientService.loadDynamicRoutes(),
    }).pipe(
      catchError(err => {
        console.error('App initialization failed:', {
          error: err.message,
          stack: err.stack,
          language: languageCode,
        });
        return of(null);
      })
    );
  };
}

// Ionic Storage configuration with preferred drivers
const storageConfig = {
  name: 'proinfra-basis-storage',
  driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
};

// PrimeNG theme and ripple effect configuration
const primeNgConfig = {
  theme: { preset: Lara },
  ripple: true,
};

// Backend base URLs, read from window.__env (populated at pod startup in
// OpenShift, see core-web-app's environments.js init container). Falls back to
// localhost for local development.
const runtimeProinfraConfig = getRuntimeProinfraConfig();

/**
 * Application configuration providing:
 * - Core Angular configurations (zone, router)
 * - HTTP client with interceptors
 * - Platform-specific providers (animations, Ionic)
 * - Storage and UI libraries (Ionic Storage, PrimeNG)
 * - Translation service with custom loader
 * - Authentication providers
 * - App initializer for translations
 * - Location strategy and locale
 */
export const appConfig: ApplicationConfig = {
  providers: [
    // Core Angular configurations
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(APP_ROUTES, withComponentInputBinding()),

    // HTTP client and interceptors
    provideHttpClient(
      withInterceptors([authInterceptor]),
      withInterceptorsFromDi()
    ),
    ...provideHttpInterceptors(),

    // Platform-specific providers
    provideAnimationsAsync(),
    provideIonicAngular({
      innerHTMLTemplatesEnabled: true,
      mode: 'md',
    }),

    // Storage and UI libraries
    importProvidersFrom(IonicStorageModule.forRoot(storageConfig)),
    providePrimeNG(primeNgConfig),

    // Translation service
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: LabelTranslationClientService,
      },
    }),

    // Authentication providers
    ...provideAuth(),

    // App initializer
    provideAppInitializer(initializeAppFactory()),

    // Miscellaneous providers
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: LOCALE_ID, useFactory: () => navigator.language || 'de' },

    // Configs due to Openshift Runtime Environment
    provideProinfraConfig(runtimeProinfraConfig),
    { provide: BASE_PATH, useValue: runtimeProinfraConfig.coreWebBackendBaseUrl },
  ],
};
